/** Shared prompts with suggestions from more than one org, reviewed together:
 *  the strongest one included takes in the rest. */
import { ChevronLeftIcon, ChevronRightIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type SharedPromptChangeGroup,
  dismissPromptSuggestion,
  getGetPromptSuggestionQueryKey,
  getListPromptSuggestionsQueryKey,
  getListSharedPromptChangesQueryKey,
  useIncorporatePromptSuggestions,
  useListSharedPromptChanges,
  useRedraftPromptSuggestion,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { PromptLink } from "@pages/Evaluations/PromptLink";
import { SuggestionDetails } from "@pages/Evaluations/PromptSuggestions";
import { CustomerSummaryButton } from "@pages/Evaluations/SuggestionApproval";
import {
  REDRAFT_POLL_MS,
  SharedChoice,
  type SharedChoiceValue,
  multiOrgSharedGroups,
  needsRedraft,
  redraftNote,
  sharedCombineRequest,
  suggestionBusy,
  suggestionDraft,
  suggestionEvaluationsPath,
} from "@pages/Evaluations/promptSuggestionFields";
import { useSuggestionCatalogs } from "@pages/Evaluations/useSuggestionCatalogs";

const CHOICE_LABELS: Record<SharedChoiceValue, string> = {
  [SharedChoice.INCLUDE]: "Include in the shared change",
  [SharedChoice.DISMISS]: "Dismiss",
};

const responseDetail = (resp: { data: unknown; status: number }): string =>
  (resp.data as { detail?: string } | undefined)?.detail ?? `HTTP ${resp.status}`;

interface SharedSuggestionsDialogProps {
  groups: SharedPromptChangeGroup[];
  onClose: () => void;
}

/** One shared prompt at a time, each org's suggestion as a full card. */
const SharedSuggestionsDialog: FC<SharedSuggestionsDialogProps> = ({ groups, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const incorporateMutation = useIncorporatePromptSuggestions();
  const redraftMutation = useRedraftPromptSuggestion();
  const [saving, setSaving] = useState(false);
  const [groupIndex, setGroupIndex] = useState(0);
  const [cardIndex, setCardIndex] = useState(0);
  // Suggestion ids are unique across prompts, so one map serves every group.
  const [choices, setChoices] = useState<Record<number, SharedChoiceValue | undefined>>({});

  const group = groups[Math.min(groupIndex, groups.length - 1)];
  const decidable = useMemo(() => group.suggestions.filter((s) => !suggestionBusy(s)), [group]);
  const busyCount = group.suggestions.length - decidable.length;
  const drafts = useMemo(() => decidable.map(suggestionDraft), [decidable]);
  const { catalogs, catalogsPending } = useSuggestionCatalogs(drafts);
  const request = sharedCombineRequest(decidable, choices);
  const shown = Math.min(cardIndex, decidable.length - 1);
  const current = decidable[shown];
  // As on its card, a stale one offers only Re-draft and Dismiss.
  const stale = current !== undefined && needsRedraft(current);
  const orgNames = [...new Set(group.suggestions.map((s) => s.organization_name))].join(", ");

  const choose = (choice: SharedChoiceValue) => {
    setChoices((prev) => ({ ...prev, [current.id]: choice }));
    if (shown < decidable.length - 1) setCardIndex(shown + 1);
  };

  const refresh = (parentId: number | null) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListSharedPromptChangesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() }),
      ...(parentId !== null
        ? [queryClient.invalidateQueries({ queryKey: getGetPromptSuggestionQueryKey(parentId) })]
        : []),
    ]);

  const redraft = async () => {
    try {
      const resp = await redraftMutation.mutateAsync({ suggestionId: current.id });
      if (resp.status !== 202) notifyError(responseDetail(resp));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Failed to re-draft the suggestion.");
    }
    await refresh(current.id);
  };

  const submit = async () => {
    if (!request) return;
    setSaving(true);
    try {
      if (request.parentId === null) {
        for (const id of request.dismiss_ids) {
          const resp = await dismissPromptSuggestion(id);
          if (resp.status !== 200) {
            notifyError(responseDetail(resp));
            // The ones before it are dismissed: show the list as it is now.
            await refresh(null);
            return;
          }
        }
        await refresh(null);
        notifySuccess("Suggestions dismissed.");
        onClose();
        return;
      }
      let combining = false;
      if (request.incorporate_ids.length > 0 || request.dismiss_ids.length > 0) {
        const resp = await incorporateMutation.mutateAsync({
          suggestionId: request.parentId,
          data: { incorporate_ids: request.incorporate_ids, dismiss_ids: request.dismiss_ids },
        });
        if (resp.status !== 200 && resp.status !== 202) {
          notifyError(resp.data.detail);
          return;
        }
        combining = resp.status === 202;
      }
      await refresh(request.parentId);
      notifySuccess(
        combining
          ? "Rio is combining the suggestions into one draft. Test it once it's ready."
          : "Combined into one draft. Test it, then review.",
      );
      onClose();
      navigate(suggestionEvaluationsPath(group.prompt_key, request.parentId));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Failed to combine the suggestions.");
      await refresh(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      setOpen={(open) => {
        if (!open) onClose();
      }}
      title={`Suggestions on a shared prompt · ${group.prompt_key}`}
      size="6xl"
      primaryButton={{
        text: saving ? "Saving…" : request?.parentId === null ? "Dismiss all" : "Combine and test",
        onClick: () => void submit(),
        disabled: request === null || saving,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose, disabled: saving }}
    >
      <div className="flex flex-col gap-y-4">
        {groups.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {groups.map((g, i) => (
              <Button
                key={g.prompt_key}
                text={g.prompt_key}
                variant="secondary"
                buttonStyle={i === groupIndex ? "filled" : "border"}
                size="sm"
                onClick={() => {
                  setGroupIndex(i);
                  setCardIndex(0);
                }}
                disabled={saving}
              />
            ))}
          </div>
        ) : null}
        <Paragraph size="small" colorType="secondary">
          {orgNames} each have suggestions for <PromptLink promptKey={group.prompt_key} />, which is on a shared
          template, so one change covers every organization on it. Include the ones to combine and dismiss the rest: the
          strongest you include takes in the others, then you test and approve it once. Get each organization&apos;s OK
          first.
          {busyCount > 0 ? ` ${busyCount} more can't be chosen while Rio re-drafts them.` : ""}
        </Paragraph>
        {current ? (
          <>
            <div className="max-h-[55vh] overflow-y-auto pr-1">
              <div className="flex flex-col gap-y-4">
                <SuggestionDetails
                  suggestion={current}
                  draft={drafts[shown]}
                  catalogs={catalogs}
                  catalogsPending={catalogsPending}
                  orgName={current.organization_name}
                />
              </div>
            </div>
            {stale ? (
              <p className="text-xs font-medium text-text-primary">{redraftNote(current, drafts[shown])}</p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-4">
              <div className="flex items-center gap-x-2">
                <button
                  type="button"
                  aria-label="Previous suggestion"
                  onClick={() => setCardIndex(shown - 1)}
                  disabled={shown === 0}
                  className="rounded-md border border-border-medium p-1 text-text-secondary disabled:opacity-40"
                >
                  <ChevronLeftIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label="Next suggestion"
                  onClick={() => setCardIndex(shown + 1)}
                  disabled={shown >= decidable.length - 1}
                  className="rounded-md border border-border-medium p-1 text-text-secondary disabled:opacity-40"
                >
                  <ChevronRightIcon className="size-4" />
                </button>
                <span className="text-xs text-text-secondary">
                  {current.organization_name} · {shown + 1} of {decidable.length} ·{" "}
                  {decidable.filter((s) => choices[s.id]).length} decided
                </span>
              </div>
              <div className="flex items-center gap-x-2">
                <CustomerSummaryButton suggestion={current} />
                {stale ? (
                  <Button
                    text={redraftMutation.isPending ? "Re-drafting…" : "Re-draft"}
                    variant="primary"
                    buttonStyle="filled"
                    size="sm"
                    onClick={() => void redraft()}
                    disabled={!current.can_redraft || redraftMutation.isPending || saving}
                  />
                ) : null}
                <div role="radiogroup" aria-label={current.theme_title} className="flex gap-x-2">
                  {Object.values(SharedChoice)
                    .filter((choice) => !stale || choice === SharedChoice.DISMISS)
                    .map((choice) => (
                      <Button
                        key={choice}
                        role="radio"
                        aria-checked={choices[current.id] === choice}
                        text={CHOICE_LABELS[choice]}
                        variant={choice === SharedChoice.INCLUDE ? "primary" : "secondary"}
                        buttonStyle={choices[current.id] === choice ? "filled" : "border"}
                        size="sm"
                        onClick={() => choose(choice)}
                      />
                    ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <Paragraph size="small" colorType="secondary">
            Rio is re-drafting every suggestion for this prompt. Check back in a few minutes.
          </Paragraph>
        )}
      </div>
    </Dialog>
  );
};

/** The button on Eval Results, only while a shared prompt has suggestions from
 *  more than one org: every such prompt, or only those one org is in. */
export const SharedPromptSuggestions: FC<{ orgId: number | null }> = ({ orgId }) => {
  const [open, setOpen] = useState(false);
  const { data } = useListSharedPromptChanges({
    query: {
      // Until a re-draft writes back.
      refetchInterval: (query) => {
        const resp = query.state.data;
        return resp?.status === 200 && resp.data.groups.some((g) => g.suggestions.some((s) => s.redraft_in_progress))
          ? REDRAFT_POLL_MS
          : false;
      },
    },
  });
  const groups = useMemo(() => {
    const all = data?.status === 200 ? multiOrgSharedGroups(data.data.groups) : [];
    return orgId === null ? all : all.filter((g) => g.suggestions.some((s) => s.organization_id === orgId));
  }, [data, orgId]);
  if (groups.length === 0) return null;

  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blurple-200 bg-blurple-50 px-4 py-3">
      <span className="flex items-center gap-x-2 text-sm text-text-primary">
        <SparklesIcon className="size-4 text-blurple-600" aria-hidden="true" />
        {groups.length === 1 ? "A shared prompt has" : `${groups.length} shared prompts have`} suggestions from more
        than one organization. Review them together, so one change covers every organization.
      </span>
      <Button
        text="Review shared prompt suggestions"
        variant="primary"
        buttonStyle="filled"
        size="sm"
        onClick={() => setOpen(true)}
      />
      {open ? <SharedSuggestionsDialog groups={groups} onClose={() => setOpen(false)} /> : null}
    </section>
  );
};
